/**
 * JobsContext — single source of truth for "what's running right now"
 * across the entire app.
 *
 * Before this Context, the navbar JobsWidget and the Course Builder
 * each polled `/api/jobs` and `/api/inflight-generations` on their own
 * timers, with state duplicated locally. That's why:
 *   • The "Generating..." pill on a block lagged the navbar widget
 *   • Two open Course Builder tabs hit the API independently
 *   • There was no single place for a global floating mini-pill
 *
 * Now: one provider polls every 3 seconds, exposes:
 *   - `rows`            : the raw merged list (Arq + block_gen_jobs + video)
 *   - `runningBlockIds` : Set<blockId> for currently-running jobs
 *   - `runningCount`    : total queued+running across all kinds
 *   - `refresh()`       : force an immediate fetch (call after triggering work)
 *
 * Every consumer (JobsWidget, CourseBuilder, GlobalJobsPill) reads from
 * here. One network call serves them all.
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from './AuthContext';

const JobsContext = createContext({
  rows: [],
  runningBlockIds: new Set(),
  runningCount: 0,
  refresh: () => {},
  isLoading: false,
});

// Poll interval. Was 2s — Feb 2026 Acme incident dropped it from 3s
// to 2s to make pill updates feel more live. Jul 2026: reverted to
// 10s because the aggregate load from every open tab (JobsWidget +
// CourseBuilder + JobMonitor) was saturating the 250m-CPU production
// pod and inflating perceived latency to ~10s on every request.
// Users with active jobs see a per-block fast poller in the
// CourseBuilder itself, so this global "what's running app-wide"
// widget can afford a 10s cadence without hurting UX.
const POLL_MS = 10000;
// After N consecutive polls with zero running/queued jobs we back
// off to IDLE_POLL_MS so an idle instructor session isn't hammering
// the API 8640 times a day for a permanently-empty list.
const IDLE_POLL_MS = 30000;
const IDLE_THRESHOLD = 3;

export function JobsProvider({ children }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [runningBlockIds, setRunningBlockIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  // Track in-flight fetches so a `refresh()` doesn't double-stack.
  const inFlightRef = useRef(false);
  // Adaptive poll cadence state — persisted in refs so we can bump it
  // between poll ticks without re-triggering the effect.
  const idleStreakRef = useRef(0);
  const timerRef = useRef(null);
  const isVisibleRef = useRef(
    typeof document !== 'undefined' ? document.visibilityState === 'visible' : true
  );

  const fetchOnce = useCallback(async () => {
    if (!user || inFlightRef.current) return;
    // Skip polling when the tab is in the background — the browser
    // still runs setInterval callbacks (throttled) and hitting the
    // API for hidden tabs contributes to production pool pressure.
    // The visibilitychange listener below re-fires an immediate fetch
    // when the user returns to the tab.
    if (!isVisibleRef.current) return;
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      // Unified endpoint already merges arq + legacy + video into one shape.
      // 2026-08-04 — window widened 2h → 6h so JobsWidget (which used to
      // poll `/api/jobs?recent_hours=6` on its own timer) can now read
      // straight off this context. One fetch serves both the navbar
      // dropdown and the CourseBuilder / GlobalJobsPill consumers,
      // cutting a whole ~10× QPS class of prod traffic.
      const res = await api.get('/jobs', { params: { limit: 50, recent_hours: 6 } });
      const allRows = res.data?.rows || [];
      setRows(allRows);

      const ids = new Set();
      let liveJobs = 0;
      for (const j of allRows) {
        if (j.status !== 'running' && j.status !== 'queued') continue;
        liveJobs += 1;
        const p = j.params || {};
        if (p.blockId) ids.add(p.blockId);
      }
      // Bump / reset idle streak — cadence is recomputed on the next tick.
      if (liveJobs === 0) {
        idleStreakRef.current = Math.min(idleStreakRef.current + 1, IDLE_THRESHOLD);
      } else {
        idleStreakRef.current = 0;
      }
      setRunningBlockIds((prev) => {
        if (prev.size === ids.size && [...prev].every((x) => ids.has(x))) return prev;
        return ids;
      });
    } catch (e) {
      // Silent — we don't want a 5xx blip to spam toasts. The user
      // will see stale "Generating" pills for ~10s, that's fine.
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [user]);

  // Polling loop. Self-scheduling setTimeout so we can vary the cadence
  // per tick based on the idle streak (fast when jobs are live, slow
  // when the queue has been empty for a while). Skipped entirely when
  // no user is logged in.
  useEffect(() => {
    if (!user) {
      setRows([]);
      setRunningBlockIds(new Set());
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await fetchOnce();
      if (cancelled) return;
      const nextDelay = idleStreakRef.current >= IDLE_THRESHOLD ? IDLE_POLL_MS : POLL_MS;
      timerRef.current = setTimeout(tick, nextDelay);
    };
    // Kick off immediately.
    tick();
    // Re-run a fetch as soon as the tab becomes visible again so the
    // user doesn't stare at a stale pill for up to 30s after switching
    // back from another tab.
    const onVis = () => {
      const visible = document.visibilityState === 'visible';
      isVisibleRef.current = visible;
      if (visible) {
        // Reset idle streak so we go fast for at least one poll.
        idleStreakRef.current = 0;
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        tick();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }
    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
    };
  }, [user, fetchOnce]);

  const runningCount = rows.filter(
    // 2026-02-24 — `awaiting_review` rows are pipeline-paused human
    // gates, not active work. They should NOT inflate the "N jobs
    // running" pill count — the user's mental model is "how many are
    // still going" (correct here) not "how many need my attention"
    // (that's the notifications tray).
    (j) => j.status === 'running' || j.status === 'queued',
  ).length;

  const awaitingReviewCount = rows.filter((j) => j.status === 'awaiting_review').length;

  return (
    <JobsContext.Provider
      value={{
        rows,
        runningBlockIds,
        runningCount,
        awaitingReviewCount,
        refresh: fetchOnce,
        isLoading,
      }}
    >
      {children}
    </JobsContext.Provider>
  );
}

export function useJobs() {
  return useContext(JobsContext);
}
